import type {QueryFunctionContext, UseQueryOptions} from '@tanstack/react-query'
import {keepPreviousData, queryOptions, skipToken, useQuery} from '@tanstack/react-query'

import type {Message, MinimalMessage} from '@/models/messageModel'
import type {Thread} from '@/models/threadModel'
import {getMessages} from '@/services/messageService'
import {NO_REFETCH_OPTIONS} from '@/utils/query/constants'

export function getMessagesQueryKey(params: {threadId: Thread['id'] | undefined}) {
  return ['messages', params.threadId] as const
}

export function getChatMessagesQueryOptions<TData = (Message | MinimalMessage)[], TError = Error>(
  params: {threadId: Thread['id'] | undefined},
  options?: Omit<UseQueryOptions<(Message | MinimalMessage)[], TError, TData>, 'queryKey'>,
) {
  return queryOptions({
    queryKey: getMessagesQueryKey(params),
    queryFn: async ({signal}: QueryFunctionContext) => {
      if (!params.threadId) return []
      return await getMessages({threadId: params.threadId, signal})
    },
    placeholderData: keepPreviousData,
    ...NO_REFETCH_OPTIONS,
    ...options,
  })
}

// Observe query - only subscribe to data, don't fetch
export function getChatMessagesObserveQueryOptions<
  TData = (Message | MinimalMessage)[],
  TError = Error,
>(
  params: {threadId: Thread['id'] | undefined},
  options?: Omit<UseQueryOptions<(Message | MinimalMessage)[], TError, TData>, 'queryKey'>,
) {
  return getChatMessagesQueryOptions<TData, TError>(params, {
    enabled: false,
    queryFn: skipToken,
    ...options,
  })
}

export function getSetChatMessagesParams(
  params: {threadId: Thread['id'] | undefined},
  updater: (messages: (Message | MinimalMessage)[] | undefined) => (Message | MinimalMessage)[],
) {
  return [getMessagesQueryKey(params), updater] as const
}

export default function useChatMessages<TData = (Message | MinimalMessage)[], TError = Error>(
  params: {threadId: Thread['id'] | undefined},
  options?: Omit<UseQueryOptions<(Message | MinimalMessage)[], TError, TData>, 'queryKey'>,
) {
  return useQuery(getChatMessagesQueryOptions<TData, TError>(params, options))
}
